/*******************************************************************
* PAGE        : AmcWarrantyManagamentDetails
* REMARKS     :
* CREATED ON  : 19-05-2026
* AUTHOR      : Gurjar.Sonal
* MODIFY      : 
* MODIFY ON   : 
********************************************************************/

import { useState } from "react";
import ButtonComponent from "../../components/ButtonComponent/ButtonComponent";
import DatePickerComponent from "../../components/DatePickerComponent/DatePickerComponent";
import { RtfToHtml } from "../../components/EditorComponent/RtfToHtml";
import { CommonFormatDate, IsEmpty } from "../../utils/AppCommonConstants";
import "./AmcWarrantyManagamentDetailsPage.css";

const UserIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="8" r="4" />
        <path d="M5 20v-1a7 7 0 0114 0v1" />
    </svg>
);

const ContractIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 12l2 2 4-4" />
        <path d="M7 3H5a2 2 0 00-2 2v16a2 2 0 002 2h14a2 2 0 002-2V8L14 3H7z" />
    </svg>
);

function Field({ label, value, variant }) {
    return (
        <div className="amc-field" >
            <span className="amc-field-label">{label}</span>
            <span className={`amc-field-value ${variant || ""}`}>{value || "-"}</span>
        </div>
    );
}

function AmcWarrantyManagamentDetailsPage({ DetailedData, setSnackbarStates, handleCloseModal }) {
    const detailedData = DetailedData ? DetailedData : {};

    const [error, setError] = useState("")

    const [overdueServiceDate, setOverdueServiceDate] = useState(() => {
        const date = new Date();
        date.setDate(date.getDate() + 4);

        return date.toISOString().split("T")[0];
    });

    const calculateRemainingDays = () => {
        const today = new Date();
        const targetDate = new Date(overdueServiceDate);
        const diffTime = targetDate - today;
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    };

    const remainingServices = Math.max(0, (Number(detailedData?.NoOfFreeService) || 0) - (Number(detailedData?.NoOfDoneServices) || 0));

    const handleOnCloseModal = () => {
        const date = overdueServiceDate
            ? new Date(overdueServiceDate)
            : null;

        if (!date) {
            setError("Date is required");
            setSnackbarStates({ isSnackbarOpen: false, snackbarMessage: 'Please Add Date', snackbarSeverity: 'error' });
            return;
        } else {
            setOverdueServiceDate(() => {
                const date = new Date();
                date.setDate(date.getDate() + 4);

                return date.toISOString().split("T")[0];
            });
            setSnackbarStates({ isSnackbarOpen: true, snackbarMessage: 'Save successfully', snackbarSeverity: 'success' });
            handleCloseModal()
        }
    }

    return <div>
        <div className="amc-body">
            {/* Customer Details */}
            <div className="amc-section">
                <div className="amc-section-header">
                    <div className="amc-section-icon"><UserIcon /></div>
                    <span className="amc-section-title">Customer Details</span>
                </div>
                <div className="amc-section-body">
                    <Field label="Full Name" value={detailedData.AccountName} />

                    <div className="amc-field">
                        <span className="amc-field-label">Phone</span>
                        {detailedData.Phone1 || detailedData.Phone2 ? (<div>
                            {detailedData.Phone1 && (<a href={`tel:${detailedData.Phone1}`} className="cd-link amc-field-value">{detailedData.Phone1}</a>)}
                            {detailedData.Phone1 && detailedData.Phone2 && ", "}
                            {detailedData.Phone2 && (<a href={`tel:${detailedData.Phone2}`} className="cd-link amc-field-value">{detailedData.Phone2}</a>)}
                        </div>) : <span className="amc-field-value">-</span>}
                    </div>

                    <div className="amc-field">
                        <span className="amc-field-label">Email</span>
                        <span className="amc-field-value">{detailedData.EMail ? (<a href={`mailto:${detailedData.EMail}`} className="cd-link">{detailedData.EMail}</a>) : "-"}</span>
                    </div>

                    <div className="amc-field">
                        <span className="amc-field-label">City</span>
                        <span className="amc-field-value">{[detailedData.CityName, detailedData.State].filter(Boolean).join(", ") || "-"}</span>
                    </div>

                    <div className="amc-field">
                        <span className="amc-field-label">Address</span>
                        <span className="amc-field-value">{[detailedData.Address1,
                        detailedData.Address2,
                        detailedData.CityName,
                        detailedData.State,
                        detailedData.Country].filter(Boolean).join(", ") || "-"}  {/* // Removes null, undefined, and empty strings */}
                            {detailedData.Pincode ? ` - ${detailedData.Pincode}` : ""}</span>
                    </div>
                </div>
            </div>

            {/* AMC Details */}
            <div className="amc-section">
                <div className="amc-section-header">
                    <div className="amc-section-icon"><ContractIcon /></div>
                    <span className="amc-section-title">AMC Details</span>
                    <span className={`${detailedData.WarrantyStatus == "Out Of AMC" ? "amc-section-expire-tag" : "amc-section-tag"}`}>{detailedData.WarrantyStatus}</span>
                </div>
                <div className="amc-section-body">
                    <Field label="Product Name" value={detailedData.ItemName} />
                    <Field label="Item Code" value={detailedData.ItemCode} />
                    <Field label="From Date" value={CommonFormatDate(detailedData.FromPeriodDate)} />
                    <Field label="To Date" value={CommonFormatDate(detailedData.ToPeriodDate)} />
                    <Field label="Total Services" value={`${detailedData.NoOfFreeService || 0} (${detailedData.NoOfDoneServices || 0} done · ${remainingServices} remaining)`} />

                    {/* <Field label="Auto-Schedule" value={data.amc.autoSchedule}  /> */}
                    <Field label="Free Services" value={`${remainingServices} remaining`} />

                    {detailedData.WarrantyStatus !== "Out Of AMC" && remainingServices !== 0 ?
                        <div style={{ display: "flex", justifyContent: "flex-start" }}>
                            <div style={{ marginRight: "35px" }}>
                                <DatePickerComponent label={"Auto Schedule Date"}
                                    value={overdueServiceDate}
                                    onChange={(date) => {
                                        setError("")
                                        setOverdueServiceDate(date)
                                    }}
                                    error={error}
                                />
                            </div>
                            <div style={{ marginTop: "8px" }} ><Field label="Days Remaining" value={calculateRemainingDays()} /></div>
                        </div>
                        : <></>}


                    {/* Auto-renew toggle */}
                    {/* <div className="amc-toggle-row" style={{ marginTop: 4 }}>
                        <div className="amc-toggle-info">
                            <span className="amc-toggle-name">Auto-Renewal</span>
                            <span className="amc-toggle-sub">
                                {autoRenew ? "Will renew automatically on expiry" : "Manual renewal required"}
                            </span>
                        </div>
                        <div
                            className={`amc-toggle ${autoRenew ? "" : "off"}`}
                            onClick={() => setAutoRenew((v) => !v)}
                            role="switch"
                            aria-checked={autoRenew}
                            tabIndex={0}
                            onKeyDown={(e) => e.key === " " && setAutoRenew((v) => !v)}
                        />
                    </div> */}
                </div>
            </div>
        </div>

        {!IsEmpty(detailedData.TermsAndCondition1) && <div className="amc-section">
            <div className="amc-section-header">
                <span className="amc-section-title">AMC Terms & Conditions</span>
            </div>
            <div className="amc-section-body">
                {/* <Field value={`${detailedData.Remarks || "The Annual Maintenance Contract (AMC) shall remain valid for a period of 12 months from the agreement start date unless terminated earlier as per the contract terms."}`} /> */}
                <div className="amc-field" style={{ maxHeight: '300px', overflowY: 'auto' }} >
                    {/* <span className="amc-field-label">{label}</span>
                            <span className={`amc-field-value ${variant || ""}`}>{value || "-"}</span> */}
                    <div className="amc-field-value" dangerouslySetInnerHTML={{ __html: RtfToHtml(detailedData.TermsAndCondition1) }} />
                </div>
            </div>

        </div>}

        <div style={{ display: detailedData.WarrantyStatus !== "Out Of AMC" && remainingServices !== 0 ? "flex" : "none", justifyContent: "flex-end" }}><ButtonComponent onClick={() => handleOnCloseModal()}>Save Changes</ButtonComponent></div>
    </div>
}

export default AmcWarrantyManagamentDetailsPage;